import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp } from '../lib/motion';

const publicAxios = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
});

const TOTAL_STEPS = 4; // 1: problem, 2: service type, 3: contact, 4: review

// Reusable inline field component
const Field = ({ label, name, value, onChange, placeholder, type = 'text', required }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label} {required && <span className="text-orange-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
);

// Slide transition used for each step — direction-aware so "back" feels
// like it's reversing, not just repeating the same forward animation.
const stepVariants = {
  enter: (direction) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -40 : 40, opacity: 0 }),
};

const BookService = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customerName: user?.name || '',
    customerPhone: '',
    customerEmail: user?.email || '',
    printerModel: '',
    problemDescription: '',
    serviceType: 'in-shop',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const [stepError, setStepError] = useState('');

  const onDrop = (acceptedFiles) => setFile(acceptedFiles[0]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*,video/*',
    multiple: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Per-step validation — keeps the user from advancing with required
  // fields empty, without needing native HTML5 `required` to fire across
  // hidden steps (which doesn't work once fields are conditionally rendered).
  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!formData.printerModel.trim()) return 'Please enter your printer model.';
      if (!formData.problemDescription.trim()) return 'Please describe the problem.';
    }
    if (currentStep === 2) {
      if (formData.serviceType === 'doorstep') {
        if (!formData.address.trim()) return 'Please enter your street address.';
        if (!formData.city.trim()) return 'Please enter your city.';
        if (!formData.state.trim()) return 'Please enter your state.';
        if (!formData.zipCode.trim()) return 'Please enter your PIN code.';
      }
    }
    if (currentStep === 3) {
      if (!formData.customerName.trim()) return 'Please enter your name.';
      if (!formData.customerPhone.trim()) return 'Please enter your phone number.';
    }
    return '';
  };

  const goNext = () => {
    const error = validateStep(step);
    if (error) {
      setStepError(error);
      return;
    }
    setStepError('');
    setDirection(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const goBack = () => {
    setStepError('');
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleFileUpload = async () => {
    if (!file) return null;
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.url;
    } catch (error) {
      toast.error(`File upload failed: ${error.response?.data?.message || error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      let mediaUrl = null;
      if (file) {
        mediaUrl = await handleFileUpload();
        if (!mediaUrl) {
          toast.error('File upload failed. Please try again.');
          setSubmitting(false);
          return;
        }
      }
      const orderData = { ...formData, media: mediaUrl ? [mediaUrl] : [] };

      // If the user is logged in, send their token so the backend's
      // optionalAuth middleware can link this order to their account
      // (see backend/middlewares/auth.js + controllers/orders.js).
      // Guest bookings simply omit this header — createOrder stays open
      // to both either way.
      const token = localStorage.getItem('token');
      const response = await publicAxios.post('/orders', orderData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const createdOrder = response?.data?.order || {};
      const orderId = createdOrder.orderNumber || createdOrder._id || createdOrder.id || null;

      setBookingResult({
        orderId,
        serviceType: formData.serviceType,
        customerName: formData.customerName,
      });

      setFormData({
        customerName: user?.name || '',
        customerPhone: '',
        customerEmail: user?.email || '',
        printerModel: '',
        problemDescription: '',
        serviceType: 'in-shop',
        address: '',
        city: '',
        state: '',
        zipCode: '',
      });
      setFile(null);
      setStep(1);
    } catch (error) {
      toast.error(`Booking failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Confirmation view ──────────────────────────────────────────────
  if (bookingResult) {
    const { orderId, serviceType, customerName } = bookingResult;

    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md w-full bg-white border border-gray-100 rounded-xl shadow-sm p-7 text-center"
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-5"
          >
            <span className="text-2xl">✓</span>
          </motion.div>

          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {customerName ? `Thanks, ${customerName.split(' ')[0]}!` : 'Booking confirmed!'}
          </h1>
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            We've received your service request and will reach out shortly to confirm the details.
          </p>

          {orderId && (
            <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 mb-5">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Reference number</p>
              <p className="text-sm font-mono font-medium text-gray-800">{orderId}</p>
            </div>
          )}

          <div className="text-left bg-blue-50 border border-blue-100 rounded-lg px-4 py-3.5 mb-6">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">What happens next</p>
            <ul className="text-sm text-blue-800 flex flex-col gap-1.5">
              <li>• We'll call you on the number you provided to confirm details</li>
              {serviceType === 'doorstep' ? (
                <li>• An engineer will visit your address at the scheduled time</li>
              ) : (
                <li>• Drop off your device at our shop whenever it's convenient</li>
              )}
              <li>• You'll get updates as your repair progresses</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setBookingResult(null)}
              className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 text-sm font-medium py-2.5 rounded-xl transition-colors"
            >
              Book another service
            </motion.button>
            <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to={user ? '/customer/dashboard' : '/'}
                className="block bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 rounded-xl transition-colors text-center"
              >
                {user ? 'Track in dashboard' : 'Back to home'}
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  const stepLabels = ['What\'s wrong', 'Service type', 'Your details', 'Review'];

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-2xl mx-auto">

        {/* Page header */}
        <motion.div className="mb-7" initial="hidden" animate="show" variants={fadeUp}>
          <h1 className="text-2xl font-semibold text-gray-900">Book a service</h1>
          <p className="text-sm text-gray-500 mt-1">
            A few quick steps and we'll get your printer sorted.
          </p>
        </motion.div>

        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {stepLabels.map((label, i) => {
              const stepNum = i + 1;
              const isActive = stepNum === step;
              const isDone = stepNum < step;
              return (
                <div key={label} className="flex-1 flex items-center">
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <motion.div
                      animate={{
                        backgroundColor: isDone || isActive ? '#2563eb' : '#e5e7eb',
                        scale: isActive ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.25 }}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                    >
                      {isDone ? '✓' : stepNum}
                    </motion.div>
                    <span className={`text-[10px] font-medium text-center hidden sm:block ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                      {label}
                    </span>
                  </div>
                  {stepNum < TOTAL_STEPS && (
                    <div className="h-0.5 flex-1 -mx-1 mb-4 sm:mb-4 bg-gray-200 relative overflow-hidden rounded-full">
                      <motion.div
                        className="absolute inset-y-0 left-0 bg-blue-600"
                        animate={{ width: stepNum < step ? '100%' : '0%' }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step card */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 sm:p-6 shadow-sm overflow-hidden relative min-h-[320px]">
          <AnimatePresence mode="wait" custom={direction}>

            {/* ── Step 1: What's wrong ─────────────────────────── */}
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-4"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  <span>🖨️</span> What's wrong?
                </p>
                <Field
                  label="Printer model"
                  name="printerModel"
                  value={formData.printerModel}
                  onChange={handleInputChange}
                  placeholder="e.g. HP LaserJet Pro M404n"
                  required
                />
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Problem description <span className="text-orange-500">*</span>
                  </label>
                  <textarea
                    name="problemDescription"
                    value={formData.problemDescription}
                    onChange={handleInputChange}
                    placeholder="Describe what's going wrong — error messages, sounds, print quality issues, etc."
                    rows={4}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition"
                  />
                </div>

                {/* Optional photo upload lives here, since it's part of describing the issue */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Photo or video (optional)
                  </label>
                  <motion.div
                    {...getRootProps()}
                    animate={{ scale: isDragActive ? 1.01 : 1 }}
                    transition={{ duration: 0.15 }}
                    className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
                      isDragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <input {...getInputProps()} />
                    {file ? (
                      <div className="flex flex-col items-center gap-2">
                        {file.type.startsWith('image/') && (
                          <img src={URL.createObjectURL(file)} alt="Preview" className="h-20 w-auto rounded-lg object-cover" />
                        )}
                        <p className="text-xs font-medium text-green-600">{file.name}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xl">☁️</span>
                        <p className="text-xs text-gray-600">
                          {isDragActive ? 'Drop the file here' : (
                            <>Drag & drop, or <span className="text-blue-600 font-medium">browse</span></>
                          )}
                        </p>
                      </div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Service type ─────────────────────────── */}
            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-4"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  <span>🔧</span> How should we help?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'in-shop', label: 'In-shop service', desc: 'Drop off at our store' },
                    { value: 'doorstep', label: 'Doorstep service', desc: 'We come to you' },
                  ].map((opt) => (
                    <motion.button
                      key={opt.value}
                      type="button"
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setFormData((prev) => ({ ...prev, serviceType: opt.value }))}
                      className={`text-left border rounded-xl p-3.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                        formData.serviceType === opt.value
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className={`text-xs mt-0.5 ${formData.serviceType === opt.value ? 'text-blue-500' : 'text-gray-400'}`}>
                        {opt.desc}
                      </p>
                    </motion.button>
                  ))}
                </div>

                <AnimatePresence initial={false}>
                  {formData.serviceType === 'doorstep' && (
                    <motion.div
                      key="address-fields"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-3 pt-1">
                        <Field
                          label="Street address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="House no., street, area"
                          required
                        />
                        <div className="grid grid-cols-3 gap-3">
                          <Field label="City" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" required />
                          <Field label="State" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" required />
                          <Field label="PIN code" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="6-digit PIN" required />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* ── Step 3: Contact details ──────────────────────── */}
            {step === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-4"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  <span>👤</span> How do we reach you?
                </p>
                <Field
                  label="Full name"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  required
                />
                <Field
                  label="Phone number"
                  name="customerPhone"
                  type="tel"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  required
                />
                <Field
                  label="Email address (for updates)"
                  name="customerEmail"
                  type="email"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                />
                <p className="text-xs text-gray-400 -mt-1">
                  Optional — we'll send booking confirmation and status updates here.
                </p>
              </motion.div>
            )}

            {/* ── Step 4: Review ───────────────────────────────── */}
            {step === 4 && (
              <motion.div
                key="step4"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-4"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  <span>📋</span> Review your request
                </p>

                <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  <ReviewRow label="Printer" value={formData.printerModel} onEdit={() => setStep(1)} />
                  <ReviewRow label="Problem" value={formData.problemDescription} onEdit={() => setStep(1)} />
                  <ReviewRow
                    label="Service"
                    value={formData.serviceType === 'doorstep' ? 'Doorstep service' : 'In-shop service'}
                    onEdit={() => setStep(2)}
                  />
                  {formData.serviceType === 'doorstep' && (
                    <ReviewRow
                      label="Address"
                      value={`${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`}
                      onEdit={() => setStep(2)}
                    />
                  )}
                  <ReviewRow label="Name" value={formData.customerName} onEdit={() => setStep(3)} />
                  <ReviewRow label="Phone" value={formData.customerPhone} onEdit={() => setStep(3)} />
                  {formData.customerEmail && (
                    <ReviewRow label="Email" value={formData.customerEmail} onEdit={() => setStep(3)} />
                  )}
                  {file && <ReviewRow label="Attachment" value={file.name} onEdit={() => setStep(1)} />}
                </div>

                <p className="text-xs text-gray-400">
                  Double-check the details above. You can tap "Edit" on any row to go back and fix it.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Step error message */}
        <AnimatePresence>
          {stepError && (
            <motion.p
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="text-xs text-orange-600 mt-3 px-1"
            >
              {stepError}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-5">
          {step > 1 && (
            <motion.button
              type="button"
              onClick={goBack}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 text-sm font-medium py-3 rounded-xl transition-colors"
            >
              Back
            </motion.button>
          )}

          {step < TOTAL_STEPS ? (
            <motion.button
              type="button"
              onClick={goNext}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-3 rounded-xl transition-colors"
            >
              Continue
            </motion.button>
          ) : (
            <motion.button
              type="button"
              onClick={handleFinalSubmit}
              disabled={submitting || uploading}
              whileHover={{ scale: submitting || uploading ? 1 : 1.01 }}
              whileTap={{ scale: submitting || uploading ? 1 : 0.98 }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-3 rounded-xl transition-colors"
            >
              {submitting ? 'Confirming booking…' : uploading ? 'Uploading file…' : '✓ Confirm booking'}
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

// Small row used in the review step — shows a label, the entered value,
// and an Edit link that jumps back to the relevant step.
const ReviewRow = ({ label, value, onEdit }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-3 bg-white">
    <div className="min-w-0">
      <p className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 break-words">{value || '—'}</p>
    </div>
    <button
      type="button"
      onClick={onEdit}
      className="text-xs font-medium text-blue-600 hover:text-blue-700 shrink-0 transition-colors"
    >
      Edit
    </button>
  </div>
);

export default BookService;